/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Render the collection screen for an open homework run.
 * @state-owner dataset store
 * @allowed-imports React; dataset and workspace stores; shared components, selectors and hooks
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects closes collection and opens the review screen
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useOverlay } from '../../../shared/components/OverlayHost';
import { BackButton, WrapPage } from '../../../shared/components/PageShell';
import { RUN_MODE_LABEL } from '../../../shared/lib/demo-data';
import { findActivity, findRun, runName, seatQuestion } from '../../../shared/lib/selectors';
import { useDatasetStore } from '../../../shared/model/dataset-store';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { QrInviteDialog } from './QrInviteDialog';

export function RunProgress({ runId }: { runId: string }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);
  const seats = useDatasetStore((state) => state.seats);
  const closeItem = useWorkspaceUiStore((state) => state.closeItem);
  const openItem = useWorkspaceUiStore((state) => state.openItem);
  const notify = useWorkspaceUiStore((state) => state.notify);

  const run = findRun(runs, runId);
  if (!run) return null;
  const roster = seats[run.classIndex] ?? [];
  const total = findActivity(activities, run.activityId)?.questionCount ?? 0;

  const closeCollection = () => {
    useDatasetStore.getState().updateRun(runId, { status: 'ended' });
    openItem({ k: 'rev', id: runId });
    notify(t('已結束收件'));
  };

  return (
    <WrapPage maxWidth={900}>
      <div className="hd">
        <BackButton onSelect={closeItem} title={`${t('返回')} ${classes[run.classIndex].name}`} />
        <div>
          <h1>{runName(activities, run)}</h1>
          <p className="sub">{`${t(RUN_MODE_LABEL[run.mode])} · 收件中 · 截止今晚 23:59`}</p>
        </div>
        <div className="sp" />
        <button
          className="bt"
          type="button"
          onClick={() => openModal(<QrInviteDialog id={runId} kind="run" />)}
        >
          {t('QR 與連結')}
        </button>
        <button className="bt" type="button" onClick={closeCollection}>
          {t('結束收件')}
        </button>
      </div>
      <section className="panel flush">
        <header>
          <h2>{t('作答進度')}</h2>
          <span className="n">
            {`${roster.filter((seat) => seat.progress > 15).length} / ${roster.length} 已交`}
          </span>
        </header>
        <div>
          {roster.map((seat) => {
            const percent = Math.min(100, Math.round((seat.progress / 20) * 100));
            return (
              <div className="it" key={seat.name}>
                <div className="av">{seat.name[0]}</div>
                <div className="t">
                  <b>{seat.name}</b>
                  <span>
                    {percent >= 95
                      ? '已交'
                      : percent > 0
                        ? `${t('寫到第')} ${seatQuestion(seat, total)} ${t('題')}`
                        : t('還沒開始')}
                  </span>
                </div>
                <span className="mtr" style={{ maxWidth: 110 }}>
                  <i style={{ width: `${percent}%` }} />
                </span>
                <em>{`${percent}%`}</em>
              </div>
            );
          })}
        </div>
      </section>
    </WrapPage>
  );
}
